/**
 * Life4Earth V2 — Auth bridge (platform-neutral).
 *
 * DEV-1418 (mobile Phase 1). client.ts used to import getAccessToken /
 * tryRefreshToken directly from src/auth/AuthContext.tsx and dispatch
 * window CustomEvents on terminal auth failures. That coupled the shared
 * API client to React-web and localStorage, making it un-importable from
 * React Native (Metro).
 *
 * This module inverts the dependency: the API client calls through a
 * registered bridge, and each platform registers its own implementation.
 *
 *   - Web:    src/auth/AuthContext.tsx registers at module scope
 *             (localStorage refresh token, window CustomEvents).
 *   - Mobile: mobile/src/auth/authBridgeSetup.ts registers at app start
 *             (Keychain refresh token, RN event emitter).
 *
 * The default bridge is a fail-closed no-op: no token, no refresh. In the
 * web app AuthContext is imported by SessionGate/App before any protected
 * fetch runs, so registration always precedes use — same ordering as the
 * old direct import, which itself only worked because AuthContext was
 * module-evaluated first.
 *
 * Keep this file free of import.meta, window, document, localStorage, and
 * DOM types — it must compile under Metro/Hermes.
 */

export interface AuthBridge {
  /** Current in-memory access token, or null when unauthenticated. */
  getAccessToken(): string | null;
  /**
   * Attempt a silent refresh; return the new access token or null.
   * Called by apiFetch exactly once per 401 response.
   */
  tryRefreshToken(): Promise<string | null>;
  /**
   * Terminal auth failure (401 + refresh impossible). Platform should wipe
   * session state and route to login. (Web: dispatches 'l4e:auth-required'.)
   */
  onAuthRequired?(): void;
  /**
   * Account suspension detected (403 ACCOUNT_SUSPENDED). Platform shows its
   * suspension surface. (Web: dispatches 'l4e:account-suspended'.)
   */
  onAccountSuspended?(detail: unknown): void;
}

const noopBridge: AuthBridge = {
  getAccessToken: () => null,
  tryRefreshToken: async () => null,
};

let _bridge: AuthBridge = noopBridge;

/** Register the platform's auth implementation. Last registration wins. */
export function setAuthBridge(bridge: AuthBridge): void {
  _bridge = bridge;
}

/** The currently registered bridge (never null; defaults to fail-closed no-op). */
export function getAuthBridge(): AuthBridge {
  return _bridge;
}
